/**
 * Service: gallery tiles (Panel B).
 *
 * A tile is a THEME row (`hobbies`) with a `gallery_slug` and a `tile_image_url`
 * bound from `marketing_reel_library`. The anon gallery reads tiles through
 * `get_public_program_by_slug` (the bytes are public in `generated-avatars`).
 *
 * The former `HOBBY_ALIASES` slug map is gone: the slug→theme bridge is the
 * `gallery_slug` column, so candidates are matched on `hobby_id` directly.
 */
import { supabase } from "@/integrations/supabase/client";
import type { LibraryItem } from "./marketing-library";

export interface ThemeTile {
  /** `hobbies.id` — the theme. */
  theme_id: string;
  /** Public tile slug (`hobbies.gallery_slug`). */
  style_id: string;
  display_name: string | null;
  emoji: string | null;
  category: string | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  preview_image_url: string | null;
  preview_library_id: string | null;
  default_sub_style_id: string | null;
}

/** Library rows that are candidate tile pictures for a theme. */
export function candidatesForTheme(themeId: string, library: LibraryItem[]): LibraryItem[] {
  return library
    .filter((l) => l.hobby_id === themeId)
    .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0));
}

/** All theme rows that have a tile slug (active or not). */
export async function listThemeTiles(): Promise<{ data: ThemeTile[]; error: Error | null }> {
  const { data, error } = await supabase
    .from("hobbies")
    .select("id, gallery_slug, tile_display_name, display_name, tile_emoji, emoji, tile_category, tile_color, tile_sort_order, tile_active, tile_image_url, tile_library_id, default_sub_style_id")
    .not("gallery_slug", "is", null)
    .order("tile_sort_order", { ascending: true });
  if (error) return { data: [], error: new Error(error.message) };
  const rows = ((data as any[]) ?? []).map((h) => ({
    theme_id: h.id,
    style_id: h.gallery_slug,
    display_name: h.tile_display_name ?? h.display_name ?? null,
    emoji: h.tile_emoji ?? h.emoji ?? null,
    category: h.tile_category ?? null,
    color: h.tile_color ?? null,
    sort_order: h.tile_sort_order ?? 0,
    is_active: !!h.tile_active,
    preview_image_url: h.tile_image_url ?? null,
    preview_library_id: h.tile_library_id ?? null,
    default_sub_style_id: h.default_sub_style_id ?? null,
  })) as ThemeTile[];
  return { data: rows, error: null };
}

/** B2 · allocate (or clear, with null) the tile picture for a slug. Unknown slug fails loudly server-side. */
export async function allocateStylePreview(
  styleId: string,
  libraryId: string | null,
): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("allocate_style_preview" as any, {
    p_style_id: styleId,
    p_library_id: libraryId,
  });
  if (error) return { url: null, error: new Error(error.message) };
  return { url: ((data as any)?.preview_image_url as string) ?? null, error: null };
}
